<template>
  <div>
    <!-- WEB VERSION -->
    <div class="carousel-title">
      <div class="columns is-vcentered">
        <div class="column is-half">{{ $t("projet.en-bref.photos") }}</div>
        <div class="column is-half">
          <div v-if="images.length !== 0" class="container has-text-right">
            <b-upload
              v-if="!readonly && images.length < maxUploadable"
              ref="fileInput"
              accept=".jpg,.jpeg,.png"
              required
              @update:model-value="addImage"
            >
              <a>
                <span>{{ $t("projet.en-bref.add-photo") }}</span>
              </a>
            </b-upload>
          </div>
        </div>
      </div>
    </div>
    <div
      style="
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 15px;
        padding: 5px;
        margin: 50px 0;
      "
    >
      <div v-if="!images || images.length === 0" style="text-align: center">
        <b-upload
          v-if="!readonly && images.length < maxUploadable"
          accept=".jpg,.jpeg,.png"
          required
          style="display: inline-block"
          @update:model-value="addImage"
        >
          <a class="button is-outlined is-info">
            <span>{{ $t("projet.en-bref.add-photo") }}</span>
          </a>
        </b-upload>
      </div>
      <div v-else class="carousel-view">
        <b-button
          v-if="!readonly && !gallery"
          class="delete-button"
          :title="$t('projet.en-bref.delete-photo')"
          size="is-small"
          rounded
          @click="confirmDelete(images[imageDisplayedIndex])"
        >
          <b-icon icon="trash" size="is-small" type="is-danger"></b-icon>
        </b-button>
        <b-carousel
          v-model="imageDisplayedIndex"
          :autoplay="false"
          indicator-custom
          :indicator-inside="false"
          :overlay="gallery"
          @update:model-value="updateDisplayedIndex"
          @click="switchGallery(true)"
        >
          <b-carousel-item v-for="(image, i) in images" :key="i" class="carousel-item">
            <div class="image-carousel-container">
              <a class="image">
                <img
                  v-if="!isDeleting"
                  :id="'img-orig-' + i"
                  class="carousel-pic"
                  :src="imagesUrl[i]"
                  crossorigin="use-credentials"
                  :alt="getAlternativeText(i)"
                  @load="convertToBase64"
                />
              </a>
            </div>
          </b-carousel-item>

          <b-button
            v-if="!readonly && gallery"
            class="delete-button gallery"
            :title="$t('projet.en-bref.delete-photo')"
            size="is-small"
            rounded
            @click="confirmDelete(images[imageDisplayedIndex])"
          >
            <b-icon icon="trash" size="is-small" type="is-danger"></b-icon>
          </b-button>
          <span v-if="gallery" class="modal-close is-large" @click="switchGallery(false)"></span>
          <template #indicators="props">
            <figure class="al image" :draggable="false">
              <img
                v-if="!isDeleting"
                :draggable="false"
                :src="imagesUrl[props.i]"
                :title="getImageTitle(props.i)"
                :alt="getAlternativeText(props.i)"
              />
            </figure>
          </template>
        </b-carousel>
      </div>
    </div>
    <b-loading v-model="isDeleting" />

    <b-message v-if="isPorteurProjet" type="is-info">
      <span v-html="$t('projet.en-bref.info-autorisation-droit-image-pp')"></span>
    </b-message>
    <b-message v-else type="is-warning">
      <span> {{ $t("projet.en-bref.info-autorisation-droit-image-acc") }} </span>
    </b-message>
    <FileWithValidation
      :label="$t(`projetPage.mon-annee.etat-des-lieux.fichier-titre`)"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      :placeholder="$t(`projet.en-bref.autorisation-droit-image-televerser`)"
      :input-notice="$t('projet.en-bref.autorisation-droit-image-input')"
      :file-operation-url="autorisationDroitImageFileUrl"
      :disabled="readonly"
      vid="autorisation_droit_image"
      :model-value="autorisationDroitImageFilePresent"
      default-file-name="AUTORISATION_DROIT_IMAGE.pdf"
      @add-file="addFile"
      @delete-file="deleteFile"
    />
  </div>
</template>

<script lang="ts">
import { BButton, BCarousel, BCarouselItem, BIcon, BLoading, BMessage, BUpload } from "buefy"
import { defineComponent, PropType } from "vue"
import { FichierDTO } from "@/model/bean/GeneratedDTOs"
import { FileService } from "@/services/rest/utils/FileService"
import { SettingsService } from "@/services/rest/utils/SettingsService"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import FileWithValidation from "@/views/inputs/FileWithValidation.vue"

export default defineComponent({
  name: "CarouselView",
  components: {
    FileWithValidation,
    BButton,
    BMessage,
    BIcon,
    BUpload,
    BLoading,
    BCarousel,
    BCarouselItem,
  },
  props: {
    projetAnnuelId: {
      type: String,
      required: true,
    },
    images: {
      type: Array as PropType<FichierDTO[]>,
      required: true,
    },
    imagesUrl: {
      type: Array as PropType<string[]>,
      required: true,
    },
    autorisationDroitImageFilePresent: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isPorteurProjet: {
      type: Boolean,
      default: false,
    },
    validationActivated: {
      type: Boolean,
      default: false,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["images-updated", "autorisation-droit-image-updated", "trigger-validation"],
  data() {
    return {
      fileService: FileService.INSTANCE,
      settingsService: SettingsService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      maxUploadable: 5,
      maxUploadSizeString: "",
      gallery: false,
      isDeleting: false,
      imageDisplayedIndex: 0,
    }
  },
  computed: {
    autorisationDroitImageFileUrl(): string {
      return this.fileService.buildUniqueFileURL(this.projetAnnuelId, "AUTORISATION_DROIT_IMAGE")
    },
  },
  methods: {
    async reloadImages(): Promise<void> {
      if (!this.projetAnnuelId) {
        return
      }

      const images = await this.fileService.getFichiers(this.projetAnnuelId, "IMAGE")
      this.$emit("images-updated", images)
    },

    addImage(files: File | File[] | null): void {
      // b-upload peut émettre File, File[] ou null selon la configuration
      const image = Array.isArray(files) ? files[0] : files

      if (this.projetAnnuelId && image) {
        this.fileService
          .uploadFichier(this.projetAnnuelId, "IMAGE", image)
          .then((_result) => {
            this.reloadImages()
            if (this.$refs.fileInput && (this.$refs.fileInput as any).$el) {
              ;(
                (this.$refs.fileInput as any).$el.querySelector(
                  'input[type="file"]',
                ) as HTMLInputElement
              ).value = ""
            }
          })
          .catch(async (error) => {
            const settings = await this.settingsService.getSettings()
            const uploadSize = parseInt(settings.MAX_UPLOAD_SIZE.toLowerCase().replace("K", ""))
            if (uploadSize > 0) {
              this.maxUploadSizeString = "(max " + Math.floor(uploadSize / 1000) + "Mo)"
            }
            this.infoReportingService.error(
              this.$t("upload-image-failed", [this.maxUploadSizeString]).toString(),
              error,
            )
          })
      }
    },

    confirmDelete(image: FichierDTO): void {
      this.$buefy.dialog.confirm({
        message: this.$t("projet.en-bref.confirm-delete").toString(),
        confirmText: this.$t("confirmer").toString(),
        cancelText: this.$t("annuler").toString(),
        onConfirm: () => this.deleteImage(image),
      })
    },

    async deleteImage(image: FichierDTO): Promise<void> {
      if (this.projetAnnuelId) {
        this.isDeleting = true
        try {
          await this.fileService.deleteFichier(this.projetAnnuelId, image.id)

          await this.reloadImages()
          this.isDeleting = false
        } catch (e: any) {
          this.isDeleting = false
        }
      }
    },

    updateDisplayedIndex(index: number): void {
      this.imageDisplayedIndex = index
    },

    switchGallery(value: boolean): void {
      this.gallery = value
      if (value) {
        return document.documentElement.classList.add("is-clipped")
      } else {
        return document.documentElement.classList.remove("is-clipped")
      }
    },

    convertToBase64(e: Event) {
      // @ts-ignore
      const origin = e.currentTarget as HTMLElement
      // @ts-ignore
      const target = document.getElementById(e.currentTarget.id.replace("orig", "clone"))
      // @ts-ignore
      const canvas = document.getElementById(e.currentTarget.id.replace("orig", "canvas"))
      // @ts-ignore
      if (origin && canvas) {
        const dataURL = this.getDataUrl(origin, canvas)
        // @ts-ignore
        target?.setAttribute("src", dataURL)
      }
    },

    getDataUrl(img: HTMLElement, canvas: HTMLElement): string {
      // Create canvas
      // @ts-ignore
      const ctx = canvas.getContext("2d")
      //@ts-ignore
      canvas.width = img.naturalWidth
      //@ts-ignore
      canvas.height = img.naturalHeight

      // Draw the image
      //@ts-ignore
      ctx.drawImage(img, 0, 0)
      // @ts-ignore convert as base 64
      return canvas.toDataURL("image/jpeg")
    },

    getImageTitle(indexImage: number | undefined): string {
      if (indexImage === undefined) return this.getAlternativeText(indexImage)
      return this.images[indexImage]?.nom
    },

    getAlternativeText(indexImage: number | undefined): string {
      const noAlternativeText = this.$t("DEFAULT.img-no-alternative-text").toString()
      if (indexImage === undefined) return noAlternativeText
      const image = this.images[indexImage]
      return image?.nom ? `${image.nom} n'a pas été trouvée.` : noAlternativeText
    },

    addFile(): void {
      this.$emit("autorisation-droit-image-updated", true)
      this.$emit("trigger-validation")
    },

    deleteFile(): void {
      this.$emit("autorisation-droit-image-updated", false)
      this.$emit("trigger-validation")
    },
  },
})
</script>

<style lang="scss">
// Imported to have access to responsive breakpoints
@import "@/styles/variables.scss";

.carousel-title {
  color: #363636;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 15px;
}

.is-active .al img {
  border: 1px solid #fff;
  filter: grayscale(0%);
}

.indicator-item {
  max-width: 100px;
}

.al img {
  border: 1px solid transparent;
  filter: grayscale(100%);
}

.pdf-gallery {
  width: 100%;

  .pdf-img {
    max-height: 400px;
    max-width: 500px;
    display: block;
    margin-left: auto;
    margin-right: auto;
  }
}

.delete-button {
  border-color: red;
  position: absolute !important;
  z-index: 10; /* Make sure it's above other content */
}

.carousel-view {
  position: relative;

  .delete-button {
    top: -2.5rem;
    right: 0;
    margin: 0;
    z-index: 0;

    &.gallery {
      top: 20px;
      right: 8rem;
      margin: 1.5rem;
      z-index: 20;
    }
  }

  .carousel.is-overlay {
    .carousel-items {
      flex-grow: 1;
      background-color: rgba(10, 10, 10, 0.86);

      .image-carousel-container {
        display: flex;
        align-items: center;
      }

      .modal-close {
        color: #555;
        margin: 1.5rem;
        background-color: #f5f5f5;
        transition: background-color 0.3s ease;

        &::before,
        &::after {
          background-color: $primary;
          transition: background-color 0.3s ease;
        }

        &:hover::before,
        &:hover::after {
          background-color: #004d66;
        }
      }
    }

    .carousel-indicator {
      background-color: rgba(10, 10, 10, 0.96);
      justify-content: center;
    }
  }
}

.carousel-arrow.is-hovered {
  opacity: 1 !important;
}

.carousel-arrow .icon.has-icons-left {
  width: 2em;
  height: 2em;
}

.carousel-arrow .icon.has-icons-right {
  width: 2em;
  height: 2em;
}

.holder-photo {
  width: 100%;
  background-color: #80808024;
  height: 500px;
  display: block;
  min-height: 50%;
}

.carousel-item {
  display: flex;
  justify-content: center;
  position: relative;
}
</style>
